// Amazon Ads profile access-level helpers (WP-744, WP-804).
//
// A profile authorization exposes per-program permissions in `accessLevels`,
// e.g. [{ apiProgram: 'campaign', accessLevel: 'edit' }, { apiProgram: 'billing',
// accessLevel: 'view' }]. The backend's `editable` flag is NOT a reliable signal
// for this — it reflects whether the current user owns the OAuth authorization
// (i.e. can manage the connection), not whether the Amazon user can edit the
// profile. The only authoritative edit-access signal is `accessLevels`.
//
// WisePPC manages PPC campaigns, so the program that matters is `campaign` — and only it.
// Other programs are routinely view-level on fully usable profiles (`billing`, `report`),
// so they must not restrict anything. Gates both connecting a profile (WP-744) and editing
// entities on the active marketplace (WP-804, via account.canEditCurrentMarketplace).

const norm = (v) => String(v ?? '').toLowerCase();

export const VIEW_ONLY_CONNECT_MESSAGE
  = 'View-only access — you need edit permission on this profile to connect it.';

// True when the user has edit access to manage campaigns on this profile.
// When access info is missing we don't block, to preserve behavior for any backend
// response that omits accessLevels.
export const hasEditAccess = (profile) => {
  const levels = profile?.accessLevels;
  if (!Array.isArray(levels) || levels.length === 0) return true;

  const campaign = levels.find((a) => norm(a?.apiProgram) === 'campaign');
  if (campaign) return norm(campaign.accessLevel) === 'edit';

  // No campaign-program entry — fall back to "has edit on any program".
  return levels.some((a) => norm(a?.accessLevel) === 'edit');
};

// True when the login cannot manage this profile's campaigns: the backend's `violations` names the
// 'campaign' program, or the profile carries permission metadata without a campaign edit entry.
// Empty or missing metadata proves nothing.
export const isMissingCampaignAccess = (profile) => {
  if (typeof profile?.violations === 'string' && profile.violations.includes("'campaign'")) {
    return true;
  }
  const levels = profile?.accessLevels;
  return (
    Array.isArray(levels) &&
    levels.length > 0 &&
    !levels.some((a) => norm(a?.apiProgram) === 'campaign' && norm(a?.accessLevel) === 'edit')
  );
};
